import { Optional, Options } from './types'
import { debug, log } from './logger'

// Trims internally so validity is a property of the email itself, not of
// whether the caller remembered to normalize first (the regex is anchored)
export const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim())

// Returns whether the event was actually posted, so callers can tell a
// dispatch apart from a skip (e.g. the iframe not listening yet)
export const dispatchEnrichDataEvent = (email?: string): boolean => {
  // Authoritative trim for the wire: the booking app validates the exact
  // string, and callers — including the public ChiliPiper.enrich() — may
  // pass padded values
  const trimmedEmail = email?.trim()
  if (!trimmedEmail) {
    log('No email value provided, skipping enrich data process')
    return false
  }
  if (!window.ChiliPiper?._marketingApi?.popup?.isListeningForDataUpdates) {
    log(
      'Chili Piper form not loaded yet, skipping enrich data process. Please ensure the form is loaded before calling ChiliPiper.enrich().'
    )
    return false
  }
  log('Dispatching enrich data event for email: ', trimmedEmail)
  const eventData = { type: 'PERFORM_PRELAUNCH', data: trimmedEmail }
  if (window.ChiliPiper?._marketingApi?.popup?.iframe) {
    window.ChiliPiper?._marketingApi?.popup?.iframe.contentWindow?.postMessage(eventData, '*')
    return true
  }
  if (window.self !== window.top) {
    window.top?.postMessage(eventData, '*')
    return true
  }
  return false
}

const DEEP_FORM_SEARCH_INTERVAL_MS = 500
const DEEP_FORM_SEARCH_MAX_ATTEMPTS = 40

export const getReachableFrameDocuments = (): Document[] =>
  Array.from(document.querySelectorAll('iframe')).reduce<Document[]>((acc, frame) => {
    try {
      if (frame.contentDocument) {
        acc.push(frame.contentDocument)
      }
    } catch {
      debug('Skipping cross-origin iframe while searching for the form')
    }
    return acc
  }, [])

export const findFormDeep = (selector: string): HTMLFormElement | undefined =>
  [document, ...getReachableFrameDocuments()]
    .map(doc => doc.querySelector<HTMLFormElement>(selector))
    .find((form): form is HTMLFormElement => form !== null)

export const waitUntilFormExistsDeep = (
  selector: string,
  callback: (form: HTMLFormElement) => void,
  attempt = 1
): void => {
  const form = findFormDeep(selector)
  if (form) {
    debug('form found: ', selector)
    callback(form)
    return
  }
  if (attempt >= DEEP_FORM_SEARCH_MAX_ATTEMPTS) {
    debug(`form not found after ${DEEP_FORM_SEARCH_MAX_ATTEMPTS} attempts, giving up: `, selector)
    return
  }
  setTimeout(
    () => waitUntilFormExistsDeep(selector, callback, attempt + 1),
    DEEP_FORM_SEARCH_INTERVAL_MS
  )
}

export function waitUntilElementExists(
  selector: string,
  callback: (elements: NodeListOf<HTMLFormElement>) => any,
  bypass = false
) {
  const el = document.querySelectorAll<HTMLFormElement>(selector)
  if (el.length) {
    debug('form found: ', selector)
    return callback(el)
  } else if (bypass) {
    return callback(el)
  }

  setTimeout(() => {
    waitUntilElementExists(selector, callback)
  }, 500)
}

export function findForms(options: Optional<Options, 'form'>) {
  if (options.query) {
    return options.query
  }

  if (options.formIds) {
    return options.formIds.map(id => `#${id}`).join(',')
  }

  if (options.formId) {
    return `#${options.formId}`
  }
  return 'form'
}

export const executeWhenDOMContentLoaded = (handler: () => void) => {
  window.addEventListener('DOMContentLoaded', () => {
    handler()
  })
}

export const executeWhenFormLoads = (
  handler: (options: Optional<Options, 'form'>, forms: NodeListOf<HTMLFormElement>) => void
) => {
  return (options: Optional<Options, 'form'>) => {
    return waitUntilElementExists(
      findForms(options),
      forms => handler({ ...options, shouldCheck: true }, forms),
      !!options.lead || !!options.form
    )
  }
}

// Throttle function in order to prevent multiple calls to the function and not need to install lodash
export const throttle = (func: (...args: any[]) => void, limit = 1000) => {
  let inThrottle: boolean
  return (...args: any[]) => {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}
